import { useInfiniteQuery } from "@tanstack/react-query";
import { instance } from "@/apis";

export interface ChatItem {
  chatId: string;
  chatTitle: string;
  userId: string;
  updatedAt: string;
}

export interface ChatListResponse {
  content: ChatItem[];
  hasNext: boolean;
  nextCursor: {
    lastUpdatedAt: string;
    lastChatId: string;
  } | null;
}

/**
 * 채팅 목록 조회 훅 (무한 스크롤 지원)
 * 커서 기반 페이징을 사용하여 채팅을 점진적으로 로드합니다.
 *
 * 동작 방식:
 * 1. 첫 요청은 커서 없이 시작
 * 2. 스크롤 시 fetchNextPage()를 호출하여 다음 페이지 로드
 * 3. hasNextPage로 더 로드할 데이터가 있는지 확인
 * 4. pages 배열에 각 페이지 데이터가 누적됨
 * 5. 브라우저 포커스 시 자동으로 첫 페이지 재검증 (백그라운드 동기화)
 */
export const useChatsQuery = () => {
  return useInfiniteQuery({
    queryKey: ["chats"],
    queryFn: async ({ pageParam }) => {
      const params: { lastUpdatedAt?: string; lastChatId?: string } = pageParam
        ? {
            lastUpdatedAt: pageParam.lastUpdatedAt,
            lastChatId: pageParam.lastChatId,
          }
        : {};

      const response = await instance.get<{ data: ChatListResponse }>(
        `/api/df/user/chat/list`,
        { params }
      );

      return response.data.data;
    },
    getNextPageParam: (lastPage) => {
      // 다음 페이지가 있으면 nextCursor 반환, 없으면 undefined
      return lastPage.hasNext && lastPage.nextCursor
        ? lastPage.nextCursor
        : undefined;
    },
    initialPageParam: undefined as { lastUpdatedAt: string; lastChatId: string } | undefined,
    // 브라우저 포커스 시 자동으로 서버와 동기화 (다른 기기에서의 변경사항 반영)
    refetchOnWindowFocus: true,
    // 5분마다 자동 재검증 (선택사항)
    staleTime: 5 * 60 * 1000,
  });
};
